import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { supabase } from '../../supabaseClient';
import {
  TrashIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  AcademicCapIcon,
  CalendarIcon,
  XMarkIcon
} from '@heroicons/react/24/solid';

export default function TutorManagementList() {
  const navigate = useNavigate();
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTutor, setSelectedTutor] = useState(null);
  const [deletionImpact, setDeletionImpact] = useState(null);
  const [loadingImpact, setLoadingImpact] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  useEffect(() => {
    fetchCurrentUser();
    fetchTutors();
  }, []);

  const fetchCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data: userData } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single();
      setCurrentUser(userData);
    }
  };

  const fetchTutors = async () => {
    try {
      // Fetch tutors with stats from server API (bypasses RLS)
      const { data } = await axios.get('/api/admin/tutors');
      const tutorsWithStats = data.tutors || [];

      setTutors(tutorsWithStats);
      console.log('Loaded tutors:', tutorsWithStats.map(t => ({ name: t.name, email: t.email, studentsCount: t.studentsCount })));
    } catch (error) {
      console.error('Error fetching tutors:', error);
      toast.error('Failed to load tutors');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = async (tutor) => {
    setSelectedTutor(tutor);
    setShowDeleteModal(true);
    setConfirmText('');

    // Fetch deletion impact
    setLoadingImpact(true);
    try {
      const { data: classrooms } = await supabase
        .from('classrooms')
        .select('id, name')
        .eq('tutor_id', tutor.id);

      const classroomIds = classrooms ? classrooms.map(c => c.id) : [];

      let students = [];
      let sessions = 0;

      if (classroomIds.length > 0) {
        // Get affected students
        const { data: enrollments } = await supabase
          .from('classroom_enrollments')
          .select('student_id, users!inner(*)')
          .in('classroom_id', classroomIds)
          .eq('is_active', true);

        const uniqueStudents = enrollments ?
          Array.from(new Map(enrollments.map(e => [e.student_id, e.users])).values()) :
          [];
        students = uniqueStudents;

        // Get sessions count
        const { data: sessionsData } = await supabase
          .from('scheduled_sessions')
          .select('id')
          .in('classroom_id', classroomIds);
        sessions = sessionsData ? sessionsData.length : 0;
      }

      setDeletionImpact({
        classrooms: classrooms || [],
        students: students || [],
        sessionsCount: sessions
      });
    } catch (error) {
      console.error('Error fetching deletion impact:', error);
      toast.error('Failed to calculate deletion impact');
    } finally {
      setLoadingImpact(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (confirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    if (!currentUser || currentUser.role !== 'admin') {
      toast.error('Admin privileges required');
      return;
    }

    setDeleting(true);
    try {
      await axios.delete(`/api/admin/delete-tutor/${selectedTutor.id}`, {
        data: {
          admin_id: currentUser.id,
          confirm: true
        }
      });

      toast.success(`Tutor ${selectedTutor.name} has been removed`);
      setShowDeleteModal(false);
      setSelectedTutor(null);
      setDeletionImpact(null);
      fetchTutors(); // Refresh list
    } catch (error) {
      console.error('Error deleting tutor:', error);
      toast.error(error.response?.data?.error || 'Failed to delete tutor');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Tutor Management
        </h1>
        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
          Manage tutors in the system
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-transparent dark:border-gray-700">
          <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Total Tutors</div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">
            {tutors.length}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-transparent dark:border-gray-700">
          <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Total Classrooms</div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">
            {tutors.reduce((sum, t) => sum + t.classroomsCount, 0)}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-transparent dark:border-gray-700">
          <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Total Students</div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">
            {tutors.reduce((sum, t) => sum + t.studentsCount, 0)}
          </div>
        </div>
      </div>

      {/* Tutors List - Mobile Cards */}
      <div className="md:hidden space-y-4">
        {tutors.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 text-center text-gray-500 dark:text-gray-400">
            No tutors found
          </div>
        ) : (
          tutors.map((tutor) => (
            <div key={tutor.id} className="bg-white dark:bg-gray-800 rounded-lg shadow border border-transparent dark:border-gray-700 p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center">
                  <div className="h-12 w-12 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold text-lg">
                    {tutor.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="ml-3">
                    <div className="font-medium text-gray-900 dark:text-white">
                      {tutor.name}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {tutor.email || <span className="text-red-500 italic">No email</span>}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteClick(tutor)}
                  className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2">
                  <div className="flex items-center justify-center gap-1 text-gray-500 dark:text-gray-400 mb-1">
                    <AcademicCapIcon className="h-4 w-4" />
                  </div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-white">{tutor.classroomsCount}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Classes</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2">
                  <div className="flex items-center justify-center gap-1 text-gray-500 dark:text-gray-400 mb-1">
                    <UserGroupIcon className="h-4 w-4" />
                  </div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-white">{tutor.studentsCount}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Students</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2">
                  <div className="flex items-center justify-center gap-1 text-gray-500 dark:text-gray-400 mb-1">
                    <CalendarIcon className="h-4 w-4" />
                  </div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-white">{tutor.sessionsCount}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Sessions</div>
                </div>
              </div>
              <div className="mt-3 text-xs text-gray-500 dark:text-gray-400 text-right">
                Joined {new Date(tutor.created_at).toLocaleDateString()}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Tutors List - Desktop Table */}
      <div className="hidden md:block bg-white dark:bg-gray-800 rounded-lg shadow border border-transparent dark:border-gray-700">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Tutor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Classrooms
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Students
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Sessions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Joined
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {tutors.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                    No tutors found
                  </td>
                </tr>
              ) : (
                tutors.map((tutor) => (
                  <tr key={tutor.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold">
                            {tutor.name.charAt(0).toUpperCase()}
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {tutor.name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {tutor.email || <span className="text-red-500 italic">No email</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      <div className="flex items-center gap-1">
                        <AcademicCapIcon className="h-4 w-4 text-gray-400" />
                        {tutor.classroomsCount}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      <div className="flex items-center gap-1">
                        <UserGroupIcon className="h-4 w-4 text-gray-400" />
                        {tutor.studentsCount}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      <div className="flex items-center gap-1">
                        <CalendarIcon className="h-4 w-4 text-gray-400" />
                        {tutor.sessionsCount}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {new Date(tutor.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleDeleteClick(tutor)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 flex items-center gap-2 ml-auto"
                      >
                        <TrashIcon className="h-5 w-5" />
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedTutor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 h-12 w-12 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                      Delete Tutor
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      This action cannot be undone
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Tutor Info */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
                <div className="font-semibold text-gray-900 dark:text-white mb-1">
                  {selectedTutor.name}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {selectedTutor.email}
                </div>
              </div>

              {/* Impact Report */}
              {loadingImpact ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500 mx-auto"></div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    Calculating impact...
                  </p>
                </div>
              ) : deletionImpact ? (
                <div className="space-y-4 mb-6">
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <h3 className="font-semibold text-red-900 dark:text-red-200 mb-3">
                      Deletion Impact:
                    </h3>

                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-red-800 dark:text-red-300">Classrooms to be deleted:</span>
                        <span className="font-bold text-red-900 dark:text-red-200">
                          {deletionImpact.classrooms.length}
                        </span>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-red-800 dark:text-red-300">Students to be unenrolled:</span>
                        <span className="font-bold text-red-900 dark:text-red-200">
                          {deletionImpact.students.length}
                        </span>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-red-800 dark:text-red-300">Sessions to be deleted:</span>
                        <span className="font-bold text-red-900 dark:text-red-200">
                          {deletionImpact.sessionsCount}
                        </span>
                      </div>
                    </div>

                    {deletionImpact.classrooms.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-red-200 dark:border-red-800">
                        <div className="text-xs font-semibold text-red-900 dark:text-red-200 mb-2">
                          Affected Classrooms:
                        </div>
                        <div className="space-y-1">
                          {deletionImpact.classrooms.map((classroom) => (
                            <div key={classroom.id} className="text-xs text-red-800 dark:text-red-300">
                              • {classroom.name}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {deletionImpact.students.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-red-200 dark:border-red-800">
                        <div className="text-xs font-semibold text-red-900 dark:text-red-200 mb-2">
                          Affected Students:
                        </div>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {deletionImpact.students.map((student, idx) => (
                            <div key={idx} className="text-xs text-red-800 dark:text-red-300">
                              • {student.name} ({student.email})
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Confirmation Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Type <span className="font-bold text-red-600">DELETE</span> to confirm:
                    </label>
                    <input
                      type="text"
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:text-white"
                      placeholder="Type DELETE"
                    />
                  </div>
                </div>
              ) : null}

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={deleting || confirmText !== 'DELETE'}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                >
                  {deleting ? 'Deleting...' : 'Delete Tutor Permanently'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
